import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/base/MangaCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { useSEO, SchemaOrg } from '@/components/base/SEO';
import { mangaList, categories, heroSlides, appDownloadInfo } from '@/mocks/manga';

const SITE_URL = 'https://beilemanga.com';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTab, setActiveTab] = useState('all');

  useSEO({
    title: '贝勒漫画 贝勒漫画网 - 贝勒漫画官网入口与app下载 | 贝勒漫画网址在线看',
    description: '贝勒漫画网是贝勒漫画官方入口，提供app下载、最新网址及在线观看服务，让您快速访问贝勒漫画所有热门资源，顺畅阅读韩漫日漫国漫。',
    keywords: '贝勒漫画网, 贝勒漫画官网, 贝勒漫画入口, 贝勒漫画网址, 贝勒漫画app, 贝勒漫画下载, 贝勒漫画在线观看, 漫画在线阅读',
    canonicalUrl: SITE_URL,
    ogType: 'website',
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const filteredManga = activeTab === 'all' ? mangaList : mangaList.filter((m) => m.category === activeTab);

  const hotManga = [...mangaList].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestManga = [...mangaList].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()).slice(0, 8);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '贝勒漫画网',
    url: SITE_URL,
    description: '贝勒漫画官方入口，提供韩漫、日漫、国漫在线观看与App下载服务',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@beilemanga.com',
      contactType: 'customer service',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '贝勒漫画网',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      'target': `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
    ],
  };

  return (
    <>
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${slide.color}`}></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 w-full">
                <span className="inline-block px-4 py-1.5 rounded-full bg-background-50/20 text-background-50 text-xs font-medium mb-4 backdrop-blur-sm whitespace-nowrap">
                  {slide.tag}
                </span>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-3 tracking-tight">
                  {slide.title}
                </h1>
                <p className="text-base md:text-xl text-background-50/90 mb-8 max-w-xl">
                  {slide.subtitle}
                </p>
                <Link
                  to={`/article/${slide.mangaId}`}
                  className="bg-primary-500 text-background-50 px-8 py-3 rounded-md text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  立即阅读
                </Link>
              </div>
            </div>
          ))}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide ? 'bg-background-50 w-6' : 'bg-background-50/40'
                }`}
                aria-label={`切换到第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto flex-wrap">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-700 hover:bg-background-200 transition-all duration-200 cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Manga Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-600 mt-1">千万读者都在追的热门漫画</p>
            </div>
            <Link to="/category/korean" className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap flex items-center gap-1 cursor-pointer">
              查看更多 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {hotManga.map((manga) => (
              <MangaCard key={manga.id} {...manga} />
            ))}
          </div>
        </section>

        {/* Latest Updates Section */}
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-600 mt-1">实时更新，永不断更</p>
              </div>
              <Link to="/category/japanese" className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap flex items-center gap-1 cursor-pointer">
                查看更多 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latestManga.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Featured Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              分类浏览
            </h2>
            <p className="text-sm text-foreground-600 mt-1">按喜好找到属于你的漫画世界</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-xl md:text-2xl w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <div className="text-center">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 hidden sm:block">{cat.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* All Manga Grid */}
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                全部漫画
              </h2>
              <p className="text-sm text-foreground-600 mt-1">探索贝勒漫画全部精彩内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {mangaList.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
          </div>
        </section>

        {/* App Download Section */}
        <section id="app-download" className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg overflow-hidden">
            <div className="flex flex-col lg:flex-row items-center gap-8 p-8 md:p-12">
              <div className="flex-1 text-background-50 text-center lg:text-left">
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-3">{appDownloadInfo.title}</h2>
                <p className="text-base md:text-lg text-background-50/90 mb-2">{appDownloadInfo.subtitle}</p>
                <p className="text-sm text-background-50/80 mb-6">{appDownloadInfo.description}</p>
                <div className="grid grid-cols-2 gap-3 mb-6 max-w-md mx-auto lg:mx-0">
                  {appDownloadInfo.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-background-50/90">
                      <i className="ri-checkbox-circle-fill w-4 h-4 flex items-center justify-center text-background-50"></i>
                      {f}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <a
                    href={appDownloadInfo.iosLink}
                    className="flex items-center gap-2 bg-background-50 text-primary-600 px-6 py-3 rounded-md text-sm font-semibold hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    rel="nofollow"
                  >
                    <i className="ri-apple-fill w-5 h-5 flex items-center justify-center"></i>
                    iOS下载
                  </a>
                  <a
                    href={appDownloadInfo.androidLink}
                    className="flex items-center gap-2 bg-background-50 text-primary-600 px-6 py-3 rounded-md text-sm font-semibold hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    rel="nofollow"
                  >
                    <i className="ri-android-fill w-5 h-5 flex items-center justify-center"></i>
                    Android下载
                  </a>
                </div>
              </div>
              <div className="hidden lg:block w-48 h-64 relative shrink-0">
                <div className="w-full h-full bg-background-50/10 rounded-lg border border-background-50/20 flex items-center justify-center">
                  <i className="ri-smartphone-line text-8xl text-background-50/30 w-20 h-20 flex items-center justify-center"></i>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '5000万+', label: '累计用户' },
                { value: '10万+', label: '漫画资源' },
                { value: '99.7%', label: '用户好评率' },
                { value: '24h', label: '全天候更新' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                  <div className="text-sm text-foreground-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}